import type { FinancePaymentMethod, ProductPaymentSettings } from '@/services';
import { Button, ConfirmActionModal, Switch } from '@/shared/ui';
import { zodFieldValidator } from '@/shared/utils';
import { Barcode, CreditCard, QrCode, type LucideIcon } from 'lucide-react';
import { usePaymentGatewayEditor } from '../hooks/use-payment-gateway-editor.hook';
import { paymentGatewayFieldsSchema } from '../schemas/payment-gateway.schema';
import {
  CARD_BILLING_MODE_OPTIONS,
  PIX_BILLING_MODE_OPTIONS,
} from '../utils/payment-gateway-options.util';
import { GatewayMethodCard } from './gateway-method-card.component';
import { SettingsChangesTable } from './settings-changes-table.component';
import { SettingsSection } from './settings-section.component';

const METHOD_ICONS: Record<FinancePaymentMethod, LucideIcon> = {
  PIX: QrCode,
  CREDIT_CARD: CreditCard,
  BOLETO_PIX: Barcode,
};

type PaymentGatewayTabProps = {
  settings: ProductPaymentSettings;
  productName: string;
  canEdit: boolean;
};

/** Aba "Cobrança e formas": liga o gateway, escolhe as formas e variações e o envio do link. */
export function PaymentGatewayTab({
  settings,
  productName,
  canEdit,
}: PaymentGatewayTabProps) {
  const {
    form,
    values,
    methodCards,
    methodsError,
    clearMethodsError,
    isLocked,
    canEnableGateway,
    pending,
    actionError,
    isSaving,
    closePending,
    confirmPending,
    handleReset,
  } = usePaymentGatewayEditor(settings, canEdit);

  const shape = paymentGatewayFieldsSchema.shape;

  return (
    <>
      <ConfirmActionModal
        isOpen={Boolean(pending)}
        onClose={closePending}
        onConfirm={() => void confirmPending()}
        isLoading={isSaving}
        errorMessage={actionError}
        title={`Salvar a cobrança de ${productName}`}
        confirmLabel='Salvar'
        description='Confira o que vai mudar. A alteração fica registrada no histórico com o seu nome.'
        warning='Vale para as próximas emissões em até 5 minutos. Apólices que já têm cobrança mantêm o modo atual; desligar o gateway deixa em espera as parcelas já agendadas.'
      >
        {pending && <SettingsChangesTable changes={pending.changes} />}
      </ConfirmActionModal>

      <form
        className='flex flex-col gap-4'
        onSubmit={event => {
          event.preventDefault();
          void form.handleSubmit();
        }}
      >
        <SettingsSection
          title='Cobrança pelo gateway (Finance.API)'
          description={
            settings.gatewaySupported
              ? 'Ligada, as formas abaixo são cobradas por link e as formas legadas ativas do catálogo continuam no combo, cobradas pelo I4Pro. Desligada, PIX, cartão e Boleto PIX saem do combo e o produto volta à cobrança do InsureMO / I4Pro.'
              : 'A proposta deste produto ainda não usa a cobrança pelo gateway. Aqui fica só o catálogo de formas.'
          }
          aside={
            <form.AppField
              name='enabled'
              children={field => (
                <Switch
                  checked={field.state.value}
                  onCheckedChange={checked => {
                    field.handleChange(checked);
                    clearMethodsError();
                  }}
                  // Desligar é sempre permitido; ligar, só onde a proposta usa o gateway.
                  disabled={
                    isLocked || (!canEnableGateway && !field.state.value)
                  }
                  label={field.state.value ? 'Ligada' : 'Desligada'}
                />
              )}
            />
          }
        />

        <SettingsSection
          title='Formas oferecidas ao corretor'
          description='Cada apólice usa uma forma só. As opções do combo vêm do catálogo do produto.'
        >
          <div className='flex flex-col gap-4'>
            {methodCards.map(card => (
              <GatewayMethodCard
                key={card.method}
                icon={METHOD_ICONS[card.method]}
                isOffered={card.isOffered}
                catalogRows={card.catalogRows}
                missingInCatalog={card.missingInCatalog}
                offerControl={
                  <form.AppField
                    name={card.field}
                    listeners={{ onChange: clearMethodsError }}
                    children={field => (
                      <field.CheckboxField
                        label={card.label}
                        disabled={isLocked || !values.enabled}
                      />
                    )}
                  />
                }
                variation={
                  card.method === 'PIX' ? (
                    <form.AppField
                      name='pixBillingMode'
                      children={field => (
                        <field.RadioCardField
                          label='PIX parcelado'
                          options={PIX_BILLING_MODE_OPTIONS}
                          direction='column'
                          disabled={
                            isLocked || !values.enabled || !card.isOffered
                          }
                        />
                      )}
                    />
                  ) : card.method === 'CREDIT_CARD' ? (
                    <form.AppField
                      name='cardBillingMode'
                      children={field => (
                        <field.RadioCardField
                          label='Cartão parcelado'
                          options={CARD_BILLING_MODE_OPTIONS}
                          direction='column'
                          disabled={
                            isLocked || !values.enabled || !card.isOffered
                          }
                        />
                      )}
                    />
                  ) : undefined
                }
              />
            ))}
            {methodsError && (
              <p role='alert' className='text-sm text-alba-error-red-600'>
                {methodsError}
              </p>
            )}
          </div>
        </SettingsSection>

        <SettingsSection title='Envio do link ao segurado'>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <form.AppField
              name='chargeLeadDays'
              validators={{ onChange: zodFieldValidator(shape.chargeLeadDays) }}
              children={field => (
                <field.NumberField
                  label='Antecedência do link (dias)'
                  helperText='Quantos dias antes de cada vencimento o link chega ao segurado.'
                  min={1}
                  max={30}
                  disabled={isLocked}
                  required
                />
              )}
            />
            <form.AppField
              name='expireGraceDays'
              validators={{
                onChange: zodFieldValidator(shape.expireGraceDays),
              }}
              children={field => (
                <field.NumberField
                  label='Dias extras até o link expirar'
                  helperText='Somados ao vencimento. O link nunca expira antes do fim do dia seguinte.'
                  min={0}
                  max={30}
                  disabled={isLocked}
                  required
                />
              )}
            />
          </div>
          <form.AppField
            name='notifyCustomerByEmail'
            children={field => (
              <field.CheckboxField
                label='Enviar o link ao segurado por e-mail'
                disabled={isLocked}
              />
            )}
          />
          <form.AppField
            name='redirectUrl'
            validators={{ onChange: zodFieldValidator(shape.redirectUrl) }}
            children={field => (
              <field.TextField
                label='Página de retorno após o pagamento (opcional)'
                placeholder='https://www.alba.com.br/pagamento-concluido'
                helperText='Vazio leva o segurado ao comprovante do Portal, com os dados do seguro. Preencha só para usar outra página.'
                maxLength={300}
                disabled={isLocked}
              />
            )}
          />
        </SettingsSection>

        {!isLocked && (
          <div className='flex justify-end gap-2'>
            <Button
              type='button'
              variant='ghost'
              size='md'
              onClick={handleReset}
              disabled={isSaving}
            >
              Descartar
            </Button>
            <Button
              type='submit'
              variant='primary'
              size='md'
              loading={isSaving}
            >
              Salvar alterações
            </Button>
          </div>
        )}
      </form>
    </>
  );
}
